import {
  AnchorButton,
  Classes,
  Intent,
  MenuItem,
  Position,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import {
  ItemRendererProps,
  MultiSelect2,
  MultiSelectProps,
} from '@blueprintjs/select';
import * as React from 'react';
import { NoResultsMenuItem, Text, Tooltip } from 'vet-bones/components';
import { escapeStringForRegExp, getIntentClassName } from 'vet-bones/utils';
import { selectItemPredicate } from 'vet-bones/utils/select';

import { EditableLabel } from 'src/components/EditableLabel';
import {
  TEST_IDS,
  getProjectMappingCompositeIdentifierItemTestId,
} from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  MappingIdentifier,
  MappingIdentifierType,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import { createNewIdentifierDelimiterItemRenderer } from 'src/containers/ProjectMapping/createNewIdentifierDelimiterItemRenderer';
import { useCompositeIdentifierPreview } from 'src/containers/ProjectMapping/hooks/compositePrimaryIdentifiers';
import { MultiSelectIdentifierItem } from 'src/containers/ProjectMapping/types';
import { projectSelector } from 'src/selectors/project';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { validateIri } from 'src/utils/validateIri';

type ProjectMappingPrimaryIdentifierMultiSelectProps = {
  classLocalName: string;
  currentItems: MultiSelectIdentifierItem[];
  currentIdentifier: MappingIdentifier;
  fieldItems: MultiSelectIdentifierItem[];
  onUpdateIri: (iri: string) => void;
  onRemove: MultiSelectProps<MultiSelectIdentifierItem>['onRemove'];
  onSelect: MultiSelectProps<MultiSelectIdentifierItem>['onItemSelect'];
  resourceData: Resource | null;
};

const itemRenderer = (
  item: MultiSelectIdentifierItem,
  { handleClick, modifiers }: ItemRendererProps
) => {
  if (!modifiers.matchesPredicate) {
    return null;
  }

  return (
    <MenuItem
      key={item.value}
      active={modifiers.active}
      onClick={handleClick}
      text={item.text}
    />
  );
};

export const ProjectMappingPrimaryIdentifierMultiSelect: React.VFC<
  ProjectMappingPrimaryIdentifierMultiSelectProps
> = ({
  classLocalName,
  currentItems,
  currentIdentifier,
  fieldItems,
  onRemove,
  onSelect,
  resourceData,
  onUpdateIri,
}) => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();

  const project = useReduxSelector(projectSelector(projectId));
  const dataNamespace = project?.dataNamespace || '';
  const DATA_NAMESPACE_PLACEHOLDER =
    copy.components.projectMapping.primaryIdentifier.dataIriNamespace;
  const DATA_NAMESPACE_PLACEHOLDER_MATCHER = new RegExp(
    `^${escapeStringForRegExp(DATA_NAMESPACE_PLACEHOLDER)}`
  );
  const defaultBaseIri = `${dataNamespace}${classLocalName}:`;

  const isIriEdited = currentIdentifier.baseIri !== '';
  const [isShowingIri, setIsShowingIri] = React.useState(isIriEdited);
  const displayBaseIri = currentIdentifier.baseIri.replace(
    new RegExp(`^${escapeStringForRegExp(dataNamespace)}`),
    DATA_NAMESPACE_PLACEHOLDER
  );
  const displayIri =
    displayBaseIri ||
    copy.components.projectMapping.primaryIdentifier.dataIriPlaceholder(
      classLocalName
    );

  const validateDiagramIri = (newIri: string, isBaseIri?: boolean) => {
    if (!diagram) {
      return '';
    }
    if (newIri === '') {
      return '';
    }

    const expandedIri = newIri.replace(
      DATA_NAMESPACE_PLACEHOLDER_MATCHER,
      dataNamespace
    );

    if (expandedIri === currentIdentifier.baseIri) {
      return '';
    }

    const validationError = validateIri(expandedIri, isBaseIri);
    if (validationError) {
      return validationError;
    }

    return '';
  };

  const preview = useCompositeIdentifierPreview({
    identifier: currentIdentifier,
    resourceData,
  });
  const errorMessage =
    currentItems.length === 0 ||
    currentItems.some((item) => item.type === MappingIdentifierType.FIELD)
      ? ''
      : copy.components.projectMapping.primaryIdentifier.fieldRequired;

  // check if the tag is a delimiter tag so the props include minimal styling
  const arrayOfIsDelimiters = currentItems.map(
    (item) => item.type === MappingIdentifierType.DELIMITER
  );
  const getTagProps = (_v: React.ReactNode, index: number) => {
    const isDelimiter = arrayOfIsDelimiters[index];
    return {
      'data-testid': getProjectMappingCompositeIdentifierItemTestId(
        index.toString()
      ),
      className: isDelimiter ? getIntentClassName('sd-delimiter') : undefined,
    };
  };

  let tooltipContent = copy.components.projectMapping.primaryIdentifier.showIri;
  if (isIriEdited) {
    tooltipContent = copy.components.projectMapping.primaryIdentifier.editedIri;
  } else if (isShowingIri) {
    tooltipContent = copy.components.projectMapping.primaryIdentifier.hideIri;
  }

  return (
    <div
      className="sd-project-mapping-composite-identifier"
      data-testid={
        TEST_IDS.projectMapping.primaryIdentifier.composite.container
      }
    >
      <div className="sd-project-mapping-composite-identifier__row">
        <MultiSelect2
          createNewItemFromQuery={(query) => ({
            text: query,
            value: query,
            type: MappingIdentifierType.DELIMITER,
          })}
          createNewItemRenderer={createNewIdentifierDelimiterItemRenderer()}
          data-testid={TEST_IDS.projectMapping.primaryIdentifier.composite.tags}
          fill
          itemPredicate={selectItemPredicate()(fieldItems)}
          itemRenderer={itemRenderer}
          items={fieldItems}
          noResults={
            <NoResultsMenuItem text={copy.components.defaults.noResults} />
          }
          onItemSelect={onSelect}
          onRemove={onRemove}
          placeholder={
            copy.components.projectMapping.primaryIdentifier.inputPlaceholder
          }
          popoverProps={{ minimal: true, popoverClassName: 'sd-popover-menu' }}
          resetOnSelect
          selectedItems={currentItems}
          tagInputProps={{
            intent: errorMessage ? Intent.DANGER : Intent.NONE,
            tagProps: getTagProps,
          }}
          tagRenderer={(item) => item.text}
        />
        <div>
          <Tooltip content={tooltipContent} position={Position.BOTTOM}>
            <AnchorButton
              active={isShowingIri}
              data-testid={
                TEST_IDS.projectMapping.primaryIdentifier.editableIriButton
              }
              disabled={isIriEdited}
              icon={IconNames.CODE}
              minimal
              onClick={() => setIsShowingIri(!isShowingIri)}
            />
          </Tooltip>
        </div>
      </div>
      <div className="sd-project-mapping-composite-identifier__row sd-project-mapping-composite-identifier__row--preview">
        {!errorMessage && preview
          ? copy.components.projectMapping.primaryIdentifier.preview
          : ''}
        {isShowingIri ? (
          <EditableLabel
            canSetEmptyString
            className={Classes.MONOSPACE_TEXT}
            errorIntent={Intent.DANGER}
            minWidth={20}
            onSetValue={(newIri) => {
              let formattedNewIri = newIri
                .trim()
                .replace(DATA_NAMESPACE_PLACEHOLDER_MATCHER, dataNamespace);

              // clear base iri if it would be same as default
              if (formattedNewIri === defaultBaseIri) {
                formattedNewIri = '';
              }
              onUpdateIri(formattedNewIri);
            }}
            onValidate={(value) => validateDiagramIri(value, true)}
            placeholder={copy.components.projectMapping.primaryIdentifier.dataIriPlaceholder(
              classLocalName
            )}
            value={displayIri}
          />
        ) : null}
        <Text
          className={isShowingIri ? Classes.MONOSPACE_TEXT : ''}
          data-testid={
            TEST_IDS.projectMapping.primaryIdentifier.composite.preview
          }
          ellipsize
        >
          {errorMessage ||
            preview ||
            copy.components.projectMapping.primaryIdentifier.emptyPreview}
        </Text>
      </div>
    </div>
  );
};
